'use client';

import {
  IconChevronLeft,
  IconChevronRight,
  IconExternalLink,
} from '@tabler/icons-react';
import React, { useCallback } from 'react';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from 'ui';
import { getHumanFileSize, localeDateFormat } from 'utils';
import { FileCardIcon, FileCardPreview, getFileType } from './FileCard';
import type { FilesDisplayerItem } from './FilesDisplayer';

// FilesDisplayer viewer: same idea as the claim documentation modal
// (`FileViewerContext`), but on the generic item — no tags, inferences or fetch.
export const FilePreviewDialog = ({
  files,
  activeFileId,
  onActiveFileIdChange,
}: {
  files: FilesDisplayerItem[];
  activeFileId: string | null;
  onActiveFileIdChange: (fileId: string | null) => void;
}) => {
  const activeIndex = files.findIndex((file) => file.id === activeFileId);
  const activeFile = files[activeIndex];

  const goToPrevious = useCallback(() => {
    const previous = files[activeIndex - 1];
    if (previous) onActiveFileIdChange(previous.id);
  }, [activeIndex, files, onActiveFileIdChange]);

  const goToNext = useCallback(() => {
    const next = files[activeIndex + 1];
    if (next) onActiveFileIdChange(next.id);
  }, [activeIndex, files, onActiveFileIdChange]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onActiveFileIdChange(null);
    },
    [onActiveFileIdChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') goToPrevious();
      if (event.key === 'ArrowRight') goToNext();
    },
    [goToNext, goToPrevious],
  );

  if (!activeFile) return null;

  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < files.length - 1;

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        className='flex h-[70vh] max-w-4xl flex-col gap-3'
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className='flex flex-row items-center justify-between gap-3 space-y-0 pr-8'>
          <div className='flex min-w-0 items-center gap-2'>
            <FileCardIcon
              className='h-4 w-4 shrink-0'
              mimetype={activeFile.mimetype || ''}
            />
            <DialogTitle className='truncate text-base'>
              {activeFile.name}
            </DialogTitle>
          </div>
          <a
            href={activeFile.url}
            target='_blank'
            rel='noreferrer'
            className='text-[13px] flex items-center gap-2 hover:underline mr-1'
          >
            <span>Abrir en una pestaña nueva</span>
            <IconExternalLink className='h-3.5 w-3.5 text-muted-foreground' />
          </a>
        </DialogHeader>

        <div className='min-h-0 flex-1 overflow-hidden rounded-lg bg-slate-50'>
          {/* `key` forces a remount per file: without it the previous image's
              zoom/pan state leaks into the next one. */}
          <FileCardPreview
            key={activeFile.id}
            type={getFileType(activeFile.mimetype)}
            previewURL={activeFile.url}
            mode='full'
            className='h-full w-full'
            imageClassName='object-contain'
          />
        </div>

        <div className='flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground'>
          <span className='truncate'>
            {getHumanFileSize(Number(activeFile.size), true)}
            {activeFile.uploadedBy ? ` · ${activeFile.uploadedBy}` : ''}
            {activeFile.uploadedAt
              ? ` · ${localeDateFormat(activeFile.uploadedAt)}`
              : ''}
          </span>

          {files.length > 1 && (
            <div className='flex shrink-0 items-center gap-1'>
              <NavButton
                direction='previous'
                disabled={!hasPrevious}
                onClick={goToPrevious}
              />
              <span className='min-w-[4.5rem] text-center'>
                {activeIndex + 1} de {files.length}
              </span>
              <NavButton
                direction='next'
                disabled={!hasNext}
                onClick={goToNext}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NavButton = ({
  direction,
  disabled,
  onClick,
}: {
  direction: 'previous' | 'next';
  disabled: boolean;
  onClick: () => void;
}) => {
  const isPrevious = direction === 'previous';
  const Icon = isPrevious ? IconChevronLeft : IconChevronRight;

  return (
    <Button
      type='button'
      variant='outline'
      size='icon'
      className='h-7 w-7'
      aria-label={isPrevious ? 'Archivo anterior' : 'Archivo siguiente'}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon className='h-4 w-4' />
    </Button>
  );
};
